import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import type { ComponentType } from 'react';

import { SpotKindIcon, TriggerKindIcon } from './icons';
import type { AiEntity, AiEntityCategory } from './types';

// The AI Scripts panel presents three modes. They map onto the richer
// `AiEntityCategory` (which still tracks landing/object for status logic):
//   trigger   — auto-plays on entry: area_entry scripts + landing
//   spot      — requires a click: ui_click scripts + 3D object POIs
//   knowledge — orphan RAG documents
export type AiScriptsMode = 'trigger' | 'spot' | 'knowledge';

export const MODE_ORDER: AiScriptsMode[] = ['spot', 'trigger', 'knowledge'];

export const categoryToMode = (category: AiEntityCategory): AiScriptsMode => {
  if (category === 'knowledge') {
    return 'knowledge';
  }
  if (category === 'spot' || category === 'object') {
    return 'spot';
  }
  // ai_trigger + landing both auto-play on entry.
  return 'trigger';
};

export const modeForEntity = (entity: AiEntity): AiScriptsMode =>
  categoryToMode(entity.category);

// The left tree groups entities into TWO sections only. AI triggers and spots
// are unified under "scripts" — a single script can be bound to both a region
// and the spots inside it ([regionName, ...spots]), so they no longer need
// separate tabs. Knowledge (orphan RAG docs) keeps its own section.
export type AiTreeSection = 'scripts' | 'knowledge';

export const TREE_SECTION_ORDER: AiTreeSection[] = ['scripts', 'knowledge'];

export const sectionForEntity = (entity: AiEntity): AiTreeSection =>
  entity.category === 'knowledge' ? 'knowledge' : 'scripts';

export interface TreeSectionMeta {
  labelKey: string;
  hintKey: string;
  icon: ComponentType<{ sx?: object }>;
}

export const TREE_SECTION_META: Record<AiTreeSection, TreeSectionMeta> = {
  scripts: {
    labelKey: 'editorMode.aiSection.scripts',
    hintKey: 'editorMode.aiSection.scriptsHint',
    icon: SmartToyOutlinedIcon,
  },
  knowledge: {
    labelKey: 'editorMode.aiMode.knowledge',
    hintKey: 'editorMode.aiMode.knowledgeHint',
    icon: LibraryBooksOutlinedIcon,
  },
};

export interface KindTokens {
  main: string;
  text: string;
  bg: string;
  border: string;
  selBg: string;
  selBorder: string;
}

// Design spec § 07. Trigger = violet (Noa accent family), Spot = teal,
// Knowledge = neutral grey.
export const KIND_TOKENS: Record<AiScriptsMode, KindTokens> = {
  trigger: {
    main: '#8B7AF8',
    text: '#CDC3FF',
    bg: 'rgba(139, 122, 248, 0.16)',
    border: 'rgba(139, 122, 248, 0.38)',
    selBg: 'rgba(139, 122, 248, 0.12)',
    selBorder: 'rgba(139, 122, 248, 0.5)',
  },
  spot: {
    main: '#2DD4BF',
    text: '#8BEADD',
    bg: 'rgba(45, 212, 191, 0.13)',
    border: 'rgba(45, 212, 191, 0.36)',
    selBg: 'rgba(45, 212, 191, 0.1)',
    selBorder: 'rgba(45, 212, 191, 0.48)',
  },
  knowledge: {
    main: '#9aa0ad',
    text: '#c2c6d2',
    bg: 'rgba(255, 255, 255, 0.07)',
    border: 'rgba(255, 255, 255, 0.16)',
    selBg: 'rgba(255, 255, 255, 0.1)',
    selBorder: 'rgba(255, 255, 255, 0.24)',
  },
};

export const MODE_ICON: Record<
  AiScriptsMode,
  ComponentType<{ sx?: object }>
> = {
  trigger: TriggerKindIcon,
  spot: SpotKindIcon,
  knowledge: LibraryBooksOutlinedIcon,
};

export interface ModeMeta {
  labelKey: string;
  hintKey: string;
  createKey: string;
  // Trigger type assigned to scripts created from this mode.
  createTriggerType: 'area_entry' | 'ui_click';
  // Short chip text (TRIGGER / SPOT). Knowledge has no chip.
  chipKey?: string;
}

export const MODE_META: Record<AiScriptsMode, ModeMeta> = {
  spot: {
    labelKey: 'editorMode.aiMode.spot',
    hintKey: 'editorMode.aiMode.spotHint',
    createKey: 'editorMode.aiMode.createSpot',
    createTriggerType: 'ui_click',
    chipKey: 'editorMode.aiKindChip.spot',
  },
  trigger: {
    labelKey: 'editorMode.aiMode.trigger',
    hintKey: 'editorMode.aiMode.triggerHint',
    createKey: 'editorMode.aiMode.createTrigger',
    createTriggerType: 'area_entry',
    chipKey: 'editorMode.aiKindChip.trigger',
  },
  knowledge: {
    labelKey: 'editorMode.aiMode.knowledge',
    hintKey: 'editorMode.aiMode.knowledgeHint',
    createKey: 'editorMode.aiMode.addKnowledge',
    createTriggerType: 'area_entry',
  },
};
